/**
 * Locations Hub Page - Lists all UniXperts offices
 * /locations
 */
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '@/context/ThemeContext';
import SEO from '@/components/SEO';
import { 
  MapPin, 
  Phone, 
  Clock, 
  ChevronRight,
  Building2,
  Users,
  Globe,
  Loader2,
  Navigation
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useNearestOffice } from '@/hooks/useNearestOffice';

const API_URL = window.location.origin || '';

const LocationsPage = () => {
  const { isDark } = useTheme();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageContent, setPageContent] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [locRes, cmsRes] = await Promise.all([
          fetch(`${API_URL}/api/locations/public`),
          fetch(`${API_URL}/api/cms/pages/public/locations-page`),
        ]);
        if (locRes.ok) setLocations(await locRes.json());
        if (cmsRes.ok) {
          const cms = await cmsRes.json();
          setPageContent(cms?.content || null);
        }
      } catch (error) {
        console.error('Error fetching locations:', error);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // CMS-backed hero / stats / cta with sensible fallbacks so the page still renders
  // if the CMS entry is missing or a specific field is left blank.
  const hero = pageContent?.hero || {};
  const heroBadge = hero.badge_text || '11 Offices Across India';
  const heroTitle = hero.title || 'Visit Us';
  const heroHighlight = hero.title_highlight || 'Near You';
  const heroSubtitle = hero.subtitle || 'Walk into any of our 11 offices for a free consultation. Our expert counselors are ready to guide you on your study abroad journey.';

  const stats = Array.isArray(pageContent?.stats) && pageContent.stats.length > 0
    ? pageContent.stats
    : [
        { value: '11',   label: 'Offices' },
        { value: '8',    label: 'States' },
        { value: '30K+', label: 'Students Helped' },
      ];

  const cta = pageContent?.cta || {};
  const ctaTitle = cta.title || "Can't Visit an Office?";
  const ctaSubtitle = cta.subtitle || 'No worries! Book a free online consultation with our expert counselors from anywhere in India.';
  const ctaPrimaryText = cta.cta_primary_text || 'Book Free Consultation';
  const ctaPrimaryLink = cta.cta_primary_link || '/contact';
  const ctaSecondaryText = cta.cta_secondary_text || 'Call: +91 9639 557 799';
  const ctaSecondaryLink = cta.cta_secondary_link || 'tel:+919639557799';

  // Nearest-office resolver (browser geolocation + haversine)
  const { status: geoStatus, nearest, distanceKm } = useNearestOffice(locations);
  const nearestSlug = nearest?.slug || null;

  // Group locations by state
  const locationsByState = locations.reduce((acc, loc) => {
    if (!acc[loc.state]) acc[loc.state] = [];
    acc[loc.state].push(loc);
    return acc;
  }, {});

  const stateOrder = Array.isArray(pageContent?.state_order) && pageContent.state_order.length > 0
    ? pageContent.state_order
    : ['Haryana', 'Gujarat', 'Maharashtra', 'Telangana', 'Andhra Pradesh', 'Karnataka', 'Kerala', 'Tamil Nadu'];

  return (
    <>
      <SEO 
        title={pageContent?.seo?.title || "Our Office Locations - Study Abroad Consultants Across India"}
        description={pageContent?.seo?.description || "Visit UniXperts study abroad consultants at 11 offices across India - Gurgaon, Ahmedabad, Pune, Hyderabad, Vijayawada, Visakhapatnam, Bengaluru, Kochi, Kottayam & Chennai. Free consultation available."}
        keywords="study abroad consultants India, overseas education offices, UniXperts branches, study abroad near me, education consultants Ahmedabad, overseas consultants Pune, study abroad Hyderabad, education consultants Bengaluru"
        canonical="https://unixperts.com/locations"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "UniXperts",
          "url": "https://unixperts.com",
          "numberOfEmployees": "100+",
          "areaServed": "India",
          "location": locations.map(loc => ({
            "@type": "Place",
            "name": `UniXperts ${loc.city}`,
            "address": {
              "@type": "PostalAddress",
              "streetAddress": loc.address,
              "addressLocality": loc.city,
              "addressRegion": loc.state,
              "addressCountry": "IN"
            }
          }))
        }}
      />
      
      <div className={`min-h-screen pt-20 ${isDark ? 'bg-[#1a2830]' : 'bg-slate-50'}`}>
        {/* Hero Section */}
        <section className={`py-16 ${isDark ? 'bg-gradient-to-br from-[#1a2830] to-[#0d1117]' : 'bg-gradient-to-br from-ux-blue to-ux-cyan'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6" data-testid="locations-hero-badge">
              <Building2 className="w-4 h-4 text-white" />
              <span className="text-white/90 text-sm font-medium">{heroBadge}</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 font-display" data-testid="locations-hero-title">
              {heroTitle}{heroHighlight ? <span className="ml-2">{heroHighlight}</span> : null}
            </h1>
            <p className="text-xl text-white/80 max-w-3xl mx-auto" data-testid="locations-hero-subtitle">
              {heroSubtitle}
            </p>
            
            {/* Stats */}
            <div className="flex flex-wrap justify-center gap-8 mt-10" data-testid="locations-hero-stats">
              {stats.map((s, i) => (
                <div key={`${s.label}-${i}`} className="text-center">
                  <div className="text-3xl font-bold text-white">{s.value}</div>
                  <div className="text-white/70 text-sm">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Locations Grid */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Nearest Office Banner */}
            {nearest && geoStatus === 'ready' && (
              <div
                className={`mb-10 rounded-2xl border p-5 md:p-6 ${
                  isDark
                    ? 'bg-gradient-to-r from-ux-cyan/10 via-ux-blue/10 to-transparent border-ux-cyan/30'
                    : 'bg-gradient-to-r from-ux-cyan/5 via-ux-blue/5 to-transparent border-ux-blue/20'
                }`}
                data-testid="nearest-office-banner"
              >
                <div className="flex flex-col md:flex-row md:items-center gap-4">
                  <div className={`p-3 rounded-xl ${isDark ? 'bg-ux-cyan/20' : 'bg-ux-blue/10'}`}>
                    <Navigation className={`w-6 h-6 ${isDark ? 'text-ux-cyan' : 'text-ux-blue'}`} />
                  </div>
                  <div className="flex-1">
                    <p className={`text-sm ${isDark ? 'text-white/60' : 'text-slate-500'}`}>Your nearest UniXperts office</p>
                    <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      {nearest.city}{nearest.area ? ` (${nearest.area})` : ''}
                      {distanceKm !== null && (
                        <span className={`ml-3 text-sm font-normal ${isDark ? 'text-white/60' : 'text-slate-500'}`}>
                          · about {distanceKm} km away
                        </span>
                      )}
                    </h3>
                  </div>
                  <div className="flex gap-2">
                    <Button asChild size="sm" variant="outline" className={isDark ? 'border-white/20 text-white hover:bg-white/10' : ''}>
                      <a href={`tel:${(nearest.phone || '').replace(/\s/g, '')}`} data-testid="nearest-office-call">
                        <Phone className="w-4 h-4 mr-2" /> Call
                      </a>
                    </Button>
                    <Button asChild size="sm" className="bg-gradient-to-r from-ux-cyan to-ux-blue text-white">
                      <Link to={`/locations/${nearest.slug}`} data-testid="nearest-office-view">
                        View Details <ChevronRight className="w-4 h-4 ml-1" />
                      </Link>
                    </Button>
                  </div>
                </div>
              </div>
            )}
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-8 h-8 animate-spin text-ux-blue" />
              </div>
            ) : locations.length === 0 ? (
              <div className="text-center py-16">
                <MapPin className={`w-16 h-16 mx-auto mb-4 ${isDark ? 'text-white/20' : 'text-slate-300'}`} />
                <p className={isDark ? 'text-white/60' : 'text-slate-500'}>No locations available</p>
              </div>
            ) : (
              stateOrder.map(state => {
                const stateLocations = locationsByState[state];
                if (!stateLocations) return null;
                
                return (
                  <div key={state} className="mb-12">
                    <h2 className={`text-2xl font-bold mb-6 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      {state}
                    </h2>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {stateLocations.map(location => {
                        const isNearest = nearestSlug === location.slug;
                        return (
                        <Link 
                          key={location.id} 
                          to={`/locations/${location.slug}`}
                          className="block group"
                          data-testid={isNearest ? 'nearest-office-card' : undefined}
                        >
                          <Card className={`h-full transition-all duration-300 hover:shadow-xl relative ${
                          isNearest
                            ? (isDark ? 'ring-2 ring-ux-cyan bg-ux-cyan/5' : 'ring-2 ring-ux-blue bg-ux-blue/5')
                            : (isDark 
                              ? 'bg-white/5 border-white/10 hover:bg-white/10' 
                              : 'bg-white hover:shadow-ux-blue/20')
                        }`}>
                          {isNearest && (
                            <span
                              className="absolute -top-3 left-4 px-3 py-1 text-xs font-semibold rounded-full bg-gradient-to-r from-ux-cyan to-ux-blue text-white shadow-md flex items-center gap-1"
                            >
                              <Navigation className="w-3 h-3" /> Nearest to you
                            </span>
                          )}
                          <CardContent className="p-6">
                            <div className="flex items-start justify-between mb-4">
                              <div>
                                <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                                  {location.city}
                                  {location.area && (
                                    <span className={`text-sm font-normal ml-2 ${isDark ? 'text-white/60' : 'text-slate-500'}`}>
                                      ({location.area})
                                    </span>
                                  )}
                                </h3>
                                <p className={`text-sm ${isDark ? 'text-white/60' : 'text-slate-500'}`}>
                                  {location.state}
                                </p>
                              </div>
                              <div className={`p-2 rounded-lg ${isDark ? 'bg-ux-cyan/20' : 'bg-ux-cyan/10'}`}>
                                <MapPin className="w-5 h-5 text-ux-cyan" />
                              </div>
                            </div>

                            <div className={`space-y-2 text-sm ${isDark ? 'text-white/70' : 'text-slate-600'}`}>
                              <div className="flex items-center gap-2">
                                <MapPin className="w-4 h-4 flex-shrink-0" />
                                <span className="truncate">{location.address.line2}, {location.address.city}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Phone className="w-4 h-4 flex-shrink-0" />
                                <span>{location.phone}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Clock className="w-4 h-4 flex-shrink-0" />
                                <span>{location.timings}</span>
                              </div>
                            </div>

                            {/* Popular Destinations */}
                            <div className="mt-4 flex flex-wrap gap-2">
                              {(location.popular_destinations || []).slice(0, 3).map(dest => (
                                <span 
                                  key={dest}
                                  className={`px-2 py-1 text-xs rounded-full ${
                                    isDark ? 'bg-white/10 text-white/80' : 'bg-slate-100 text-slate-600'
                                  }`}
                                >
                                  {dest}
                                </span>
                              ))}
                            </div>

                            <div className={`mt-4 flex items-center text-sm font-medium group-hover:gap-2 transition-all ${
                              isDark ? 'text-ux-cyan' : 'text-ux-blue'
                            }`}>
                              View Details
                              <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                            </div>
                          </CardContent>
                        </Card>
                      </Link>
                      );
                    })}
                  </div>
                </div>
              );
            })
            )}
          </div>
        </section>

        {/* CTA Section */}
        <section className={`py-16 ${isDark ? 'bg-white/5' : 'bg-ux-blue/5'}`} data-testid="locations-cta-section">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <Globe className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-ux-cyan' : 'text-ux-blue'}`} />
            <h2 className={`text-2xl md:text-3xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`} data-testid="locations-cta-title">
              {ctaTitle}
            </h2>
            <p className={`text-lg mb-8 ${isDark ? 'text-white/70' : 'text-slate-600'}`} data-testid="locations-cta-subtitle">
              {ctaSubtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-gradient-to-r from-ux-cyan to-ux-blue text-white hover:opacity-90"
                asChild
                data-testid="locations-cta-primary"
              >
                <Link to={ctaPrimaryLink}>
                  <Phone className="w-4 h-4 mr-2" />
                  {ctaPrimaryText}
                </Link>
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className={isDark ? 'border-white/20 text-white hover:bg-white/10' : ''}
                asChild
                data-testid="locations-cta-secondary"
              >
                <a href={ctaSecondaryLink}>
                  {ctaSecondaryText}
                </a>
              </Button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default LocationsPage;
